'use client';

import * as React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Paper, Typography } from '@mui/material';
import { ConfirmProvider } from 'material-ui-confirm';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import '@/styles/theme-v2/global.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider as ReduxProvider } from 'react-redux';

import type { Metadata } from '@/types/v2/metadata';
import { config } from '@/config';
import { applyDefaultSettings } from '@/lib/v2/settings/apply-default-settings';
import { getSettings as getPersistedSettings } from '@/lib/v2/settings/get-settings';
import { UserProvider } from '@/contexts/auth/user-context';
import { SettingsProvider } from '@/contexts/settings';
import { Analytics } from '@/components/core/v2/analytics';
import { I18nProvider } from '@/components/core/v2/i18n-provider';
import { LocalizationProvider } from '@/components/core/v2/localization-provider';
import { ThemeProvider } from '@/components/core/v2/theme-provider/theme-provider';
import { ThemedToaster, Toaster } from '@/components/core/v2/toaster';

import { NotificationProvider } from '@/contexts/notifications/notification-provider';
import { FeatureFlagProvider } from './contexts/feature-flag/feature-flag-provider';
import { store } from './store';
import { EchoProvider } from '@/contexts/echo/echo-provider';
import { useEchoPrivateEvent } from '@/hooks/v2/use-echo';
import { useUser } from '@/hooks/v2/use-user';

const queryClient = new QueryClient();

const metadata: Metadata = { title: config.site.name };

interface RootProps {
  children: React.ReactNode;
}

export function Root({ children }: RootProps): React.JSX.Element {
  const settings = React.useRef(applyDefaultSettings(getPersistedSettings()));

  const appContent = (
    <Analytics>
      <LocalizationProvider>
        <FeatureFlagProvider>
          <SettingsProvider settings={settings.current}>
            <I18nProvider language="en">
              <ThemeProvider>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <ConfirmProvider>{children}</ConfirmProvider>
                  <ThemedToaster position="top-right" />
                </ErrorBoundary>
              </ThemeProvider>
            </I18nProvider>
          </SettingsProvider>
        </FeatureFlagProvider>
      </LocalizationProvider>
    </Analytics>
  );

  return (
    <HelmetProvider>
      <Helmet>
        <meta content={config.site.themeColor} name="theme-color" />
        <title>{metadata.title}</title>
      </Helmet>
      <ReduxProvider store={store}>
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            {/* EchoGate sits inside UserProvider so it can read the auth token */}
            <EchoGate appContent={appContent} />
          </UserProvider>
        </QueryClientProvider>
      </ReduxProvider>
    </HelmetProvider>
  );
}

/**
 * Reads the auth token from localStorage (set after login) and passes it to
 * EchoProvider so Echo can authenticate private channels. Re-creates the Echo
 * connection when the user logs in or out.
 */
function EchoGate({ appContent }: { appContent: React.ReactNode }): React.JSX.Element {
  const { user } = useUser();
  const token = user ? localStorage.getItem(config.api.token) : null;

  return (
    <EchoProvider token={token}>
      <CommunitySync />
      <NotificationProvider>{appContent}</NotificationProvider>
    </EchoProvider>
  );
}

/**
 * Listens for real-time `community.changed` events via Reverb on the
 * authenticated user's private channel. When another client (e.g. mobile app)
 * switches communities, this re-fetches the session and flushes caches so the
 * web app updates instantly without a page refresh.
 */
function CommunitySync(): null {
  const { user, checkSession } = useUser();
  const qc = React.useRef(queryClient);

  // Echo private channel name without the "private-" prefix — Echo adds it
  const channelName = user ? `user.${user.id}` : '';

  useEchoPrivateEvent(
    channelName,
    '.community.changed',
    React.useCallback((data: any) => {
      console.log('[CommunitySync] Received community.changed event:', data);
      checkSession().then(() => {
        console.log('[CommunitySync] Session refreshed, invalidating queries');
        qc.current.invalidateQueries();
      });
    }, [checkSession]),
    [channelName]
  );

  return null;
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 500,
          width: '100%',
        }}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography gutterBottom variant="h5">
          Oops! Something went wrong.
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 3 }} variant="body1">
          We're sorry for the inconvenience. Please try refreshing the page, or contact support if the problem persists.
        </Typography>
        <Button
          onClick={() => {
            window.location.reload();
          }}
          variant="contained"
        >
          Refresh Page
        </Button>
      </Paper>
    </Box>
  );
}
